import { oneOf } from "../lib/search";

/**
 * Where an activity was opened from, so its trail and its Back button lead
 * there -- the full way there, as that page's own trail reads -- rather than
 * to the viewer's own activities.
 *
 * Without it a supervisor who opened a student's activity from My group was
 * offered "Back to activities", which is their own list, and the trail did not
 * show the way they had come at all.
 *
 * Absent means the viewer's own activities, so a link that says nothing (a
 * notification, a hand-typed address) still leads somewhere sensible.
 */
const FROM = [
  "reviews",
  "dashboard",
  "group",
  "coSupervision",
  "progress",
  "myProgramme",
  "programme",
  "assigned",
  "audit",
] as const;

/** How a student's programme page was itself reached; absent is the matrix. */
const VIA = ["group", "coSupervision"] as const;

export type ActivityOrigin = {
  from?: (typeof FROM)[number];
  via?: (typeof VIA)[number];
};

export function validateActivityDetailSearch(search: Record<string, unknown>): ActivityOrigin {
  const from = oneOf(search.from, FROM);
  return { from, via: from === "programme" ? oneOf(search.via, VIA) : undefined };
}

type Crumb = { label: string; to: string; search?: Record<string, string> };

const ACTIVITIES: Crumb = { label: "Activities", to: "/activities" };
const GROUP: Crumb = { label: "My group", to: "/my-group" };
const CO_SUPERVISION: Crumb = { label: "Co-supervision", to: "/co-supervision" };
const PROGRESS: Crumb[] = [ACTIVITIES, { label: "Programme progress", to: "/activities/admin/progress" }];

/**
 * The pages between CeMMbase and the activity, and where Back leads.
 *
 * `subject` is the person whose activity it is, for the one origin that is a
 * page about them: their name is empty until the activity has loaded, which
 * the header draws as a skeleton.
 */
export function activityTrail(
  origin: ActivityOrigin,
  subject: { personId: string; name: string | null },
): { crumbs: Crumb[]; back: Crumb } {
  const crumbs = ((): Crumb[] => {
    switch (origin.from) {
      case "reviews":
        return [ACTIVITIES, { label: "My reviews", to: "/activities", search: { tab: "reviews" } }];
      // The dashboard is the root, which the first crumb already is.
      case "dashboard":
        return [];
      case "group":
        return [GROUP];
      case "coSupervision":
        return [CO_SUPERVISION];
      case "progress":
        return PROGRESS;
      case "myProgramme":
        return [{ label: "My programme", to: "/my-programme" }];
      case "programme":
        return [
          ...(origin.via === "group" ? [GROUP] : origin.via === "coSupervision" ? [CO_SUPERVISION] : PROGRESS),
          {
            label: subject.name ?? "",
            to: `/programme/${subject.personId}`,
            search: origin.via === undefined ? undefined : { from: origin.via },
          },
        ];
      case "assigned":
        return [ACTIVITIES, { label: "Assigned activities", to: "/activities/admin/assigned" }];
      case "audit":
        return [{ label: "Administration", to: "/admin" }, { label: "Audit log", to: "/admin/audit-log" }];
      default:
        return [ACTIVITIES];
    }
  })();

  const last = crumbs.at(-1);
  const back: Crumb = last === undefined
    ? { label: "Back to dashboard", to: "/" }
    : origin.from === "programme"
      ? { ...last, label: subject.name === null ? "Back to programme" : `Back to ${subject.name}'s programme` }
      : { ...last, label: `Back to ${last.label.toLowerCase()}` };

  return { crumbs, back };
}
